// The guest's page (M3): /guest#<token>. A guest has no Microsoft login, so this surface sits
// before the sign-in gate (App.tsx's line, HANDOVER-messenger.md). The token in the fragment
// never reaches a server log; it is posted once to become a cookie, and every later visit to
// /guest reads the cookie. What the guest sees is what the API returns to a guest: the chats
// they were invited into, and nothing else, drawn by the same pane as a colleague's.
//
// Who the page is for is settled in messenger/guest.ts before anything is drawn: the pane only
// for a guest. A signed-in member of staff's session wins at the API, so without that they
// were shown their own chats under a "Guest" label; they are told in words that they are
// signed in as themselves and offered Chats, and an invite link they open is not posted.
import { ActionButton, SectionHeading } from './parts/index.js';
import { useEffect, useState } from 'react';
import { Logo } from './Logo.js';
import { MessengerPane } from './Messenger.js';
import { messenger } from './messenger/api.js';
import { CHATS_LINK_WORDS, CHATS_PATH, inviteTokenFrom, settleGuest, signedInWords, type GuestPhase } from './messenger/guest.js';
import './messenger.css';

type Phase = { kind: 'booting' } | GuestPhase;

export function MessengerGuest() {
  const [phase, setPhase] = useState<Phase>({ kind: 'booting' });
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void settleGuest(messenger, inviteTokenFrom(window.location.hash)).then((next) => {
      // A posted token has become a cookie; take it out of the address bar. One that was not
      // posted (a signed-in person's) stays, so they can carry the link to a private window.
      if (next.kind === 'ready' && next.accepted) window.history.replaceState(null, '', '/guest');
      if (!cancelled) setPhase(next);
    });
    return () => { cancelled = true; };
  }, []);

  if (phase.kind === 'booting') return <main className="screen"><Logo spinning /></main>;
  if (phase.kind === 'refused') return <main className="screen"><Logo /><h1>Cobrain</h1><p className="plain">{phase.message}</p></main>;
  if (phase.kind === 'signed-in') {
    return (
      <main className="screen">
        <Logo /><h1>Cobrain</h1>
        {signedInWords(phase.name, phase.hadInvite).map((w) => <p key={w} className="plain">{w}</p>)}
        <p className="plain"><a href={CHATS_PATH}>{CHATS_LINK_WORDS}</a></p>
      </main>
    );
  }
  return (
    <div className="app">
      <header className="bar-top">
        <span className="brand"><Logo size={26} /><span>Cobrain</span></span>
        <span className="who">{phase.name} · Guest <ActionButton type="button" className="dotted" onClick={() => { void messenger.guestLeave().then(() => window.location.reload()); }}>leave</ActionButton></span>
      </header>
      <div className="shell">
        <main className="page wide">
          <MessengerPane store={null} selected={selected} onOpen={setSelected} canManage={false} title={<SectionHeading title={<>Chats</>} note={<>you see the chats you were invited into</>} />} />
        </main>
      </div>
    </div>
  );
}
